import type { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import Axios from 'axios';
import { logoutUser } from 'services/authorization/handleLogout';
import { refreshTokenFlow } from 'services/backgroundrefreshtoken';
import { resetMixpanel } from '@proposify/frontend-observability';
import { HttpCodeStatus } from '../query/HttpCodeStatus';
import { isAccountExpiredOrSuspended } from './utils';

const appAxiosRequestConfig: AxiosRequestConfig = {
  baseURL: globalThis.env.VITE_API_ENDPOINT,
};
const legacyAxiosRequestConfig: AxiosRequestConfig = {
  baseURL: globalThis.env.VITE_LEGACY_APP_URL,
  withCredentials: true,
};
const documentServiceAxiosRequestConfig: AxiosRequestConfig = {
  baseURL: globalThis.env.VITE_DOCUMENT_SERVICE_API_ENDPOINT,
};
const contentLibraryServiceAxiosRequestConfig: AxiosRequestConfig = {
  baseURL: globalThis.env.VITE_CONTENT_LIBRARY_API_ENDPOINT,
};
const notificationPreferencesAxiosRequestConfig: AxiosRequestConfig = {
  baseURL: globalThis.env.VITE_NOTIFICATION_PREFERENCES_API_ENDPOINT,
};
const customFieldsAxiosRequestConfig: AxiosRequestConfig = {
  baseURL: globalThis.env.VITE_CUSTOM_FIELDS_API_ENDPOINT,
};

const variablesBffAxiosRequestConfig: AxiosRequestConfig = {
  baseURL: globalThis.env.VITE_VARIABLES_BFF_API_ENDPOINT,
};

export const legacyClient = getAxiosClient(Axios.create(legacyAxiosRequestConfig));
export const apiClient = getAxiosClient(Axios.create(appAxiosRequestConfig));
export const variablesBffClient = getAxiosClient(Axios.create(variablesBffAxiosRequestConfig));
export const customFieldsClient = getAxiosClient(Axios.create(customFieldsAxiosRequestConfig));
export const documentServiceClient: AxiosClient = getAxiosClient(Axios.create(documentServiceAxiosRequestConfig));
export const contentLibraryServiceClient: AxiosClient = getAxiosClient(Axios.create(contentLibraryServiceAxiosRequestConfig));
export const notificationPreferencesClient = getAxiosClient(Axios.create(notificationPreferencesAxiosRequestConfig));

export type AxiosClient = ReturnType<typeof getAxiosClient>;

function getAxiosClient(axios: AxiosInstance) {
  type RequestConfig = AxiosRequestConfig & { _retry?: boolean };
  const onRejected = async (error: AxiosError<undefined | { message?: string }>) => {
    const response = error?.response;
    const originalRequestConfig: RequestConfig = error?.config;
    if (response) {
      if (isAccountExpiredOrSuspended(response)) {
        logoutUser();
        resetMixpanel();
      } else if (response.status === HttpCodeStatus.UNAUTHORIZED) {
        if (originalRequestConfig._retry !== true) {
          originalRequestConfig._retry = true;
          return refreshTokenFlow().then(() => {
            return axios(originalRequestConfig);
          });
        } else {
          logoutUser();
          resetMixpanel();
        }
      }
    }

    return Promise.reject(error);
  };

  const requestInterceptor = async (config: AxiosRequestConfig) => {
    config.withCredentials = true;
    return config;
  };

  axios.interceptors.request.use(requestInterceptor);
  axios.interceptors.response.use(undefined, onRejected);

  return {
    async get<ApiResponse = unknown>(url: string, options?: Pick<AxiosRequestConfig, 'params' | 'validateStatus'>): Promise<ApiResponse> {
      const response = await axios.get(url, options);
      return response.data;
    },
    async post<ApiResponse = unknown>(url: string, data: unknown, config?: AxiosRequestConfig): Promise<ApiResponse> {
      try {
        const response = await axios.post<ApiResponse>(url, data, config);
        return response.data;
      } catch (e) {
        const error = e as AxiosError;
        throw error.response?.data || error;
      }
    },
    async put<ApiResponse = unknown>(url: string, data?: unknown): Promise<ApiResponse> {
      const response = await axios.put(url, data);
      return response.data;
    },
    async patch<ApiResponse = unknown, TData = unknown>(url: string, data?: TData, config?: AxiosRequestConfig): Promise<ApiResponse> {
      const response = await axios.patch<ApiResponse, AxiosResponse, TData>(url, data, config);
      return response.data;
    },
    async delete<ApiResponse = unknown>(url: string): Promise<ApiResponse> {
      const response = await axios.delete(url);
      return response.data;
    },
    async deleteWithData<ApiResponse = unknown>(url: string, data: unknown): Promise<ApiResponse> {
      const response = await axios.delete(url, { data: data });
      return response.data;
    },
    async postForm<ApiResponse = unknown>(url: string, data: unknown): Promise<ApiResponse> {
      try {
        const response = await axios.postForm<ApiResponse>(url, data);
        return response.data;
      } catch (e) {
        const error = e as AxiosError;
        throw error.response?.data;
      }
    },
  };
}

export function getDocumentServiceSchemaClient() {
  return {
    baseURL: globalThis.env.VITE_DOCUMENT_SERVICE_API_HOST,
    withCredentials: true,
  };
}

export function getDocumentServicePublicClientConfig() {
  return {
    baseURL: globalThis.env.VITE_DOCUMENT_SERVICE_API_HOST,
  };
}

const documentServicePublicAxios = Axios.create({ baseURL: globalThis.env.VITE_DOCUMENT_SERVICE_API_HOST });

// No interceptors on purpose: this is the anonymous-preview path, and the shared
// documentServiceClient's 401 handler would log an anonymous viewer out of the page.
export const documentServicePublicClient = {
  async get<ApiResponse = unknown>(url: string): Promise<ApiResponse> {
    const response = await documentServicePublicAxios.get(url);
    return response.data;
  },
};

export function getUserServiceSchemaClientConfig() {
  return {
    baseURL: globalThis.env.VITE_USERS_SERVICE_API_ENDPOINT,
    withCredentials: true,
  };
}
